import { useCallback, useRef, useState } from 'react'
import { apiPost } from '../../generic/Api_Functions'
import { claimFreshIds, createQuerySequence, isBatchAnswer, launchQuery, requestedIds, upsertRequestedEntries } from './requestedEntries'

/**
 * Resolves the CU4 stock-contable-vs-mínimo status for a set of part references with a single
 * batch call (anti-N+1, same pattern as `useMainImageResolution`, S17/S18 of story
 * 45-minimum-stock-auto-replenishment).
 *
 * Contract confirmed with Sun: `POST /endpoints/partReferences/minimumStockStatus` with an array
 * of ids, responding with a map keyed by part reference id (MinimumStockStatusRest, CU4 —
 * "Contract reconciled with Jupiter" on the Sun side). References absent from the map are not
 * minimum-stock controlled (or inactive).
 *
 * The Part references list queries it for the visible page on every refresh and, while rows are
 * selected, also for the "N items selected" counter. Every query follows the same rules as the
 * main-image catalog (story #163, DL1 = a, RN13): it only updates the ids it asked for (present →
 * overwritten, absent → removed: no indicator), the query launched later wins per id, a failed or
 * empty query changes nothing (rows keep their last known indicator) and the map keeps its identity
 * when nothing changes — without that, a selected row re-queried in a loop (#2819, story #163 R1).
 *
 * @returns {[Object, (partReferenceIds: Array<number|string>) => Promise<void>]}
 *   A tuple of the status map keyed by part reference id, and its resolver.
 */
const useMinimumStockStatus = () => {
  const [minimumStockStatusMap, setMinimumStockStatusMap] = useState({})
  // RN13 / RN6 — the query launched later wins, per id; a sequence of its own, independent from the
  // thumbnail one (see requestedEntries.js).
  const querySequence = useRef(createQuerySequence())

  const resolveMinimumStockStatuses = useCallback(async (partReferenceIds) => {
    const ids = requestedIds(partReferenceIds)
    if (ids.length === 0) {
      // An empty query speaks about no reference: no request, nothing changes (RN13 / RN8).
      return
    }
    const queryNumber = launchQuery(querySequence.current)
    try {
      // One batch call per visible page; never one request per row (anti-N+1, S17).
      const map = await apiPost('partReferences/minimumStockStatus', ids)
      if (!isBatchAnswer(map)) {
        return
      }
      const freshIds = claimFreshIds(querySequence.current, ids, queryNumber)
      setMinimumStockStatusMap((previous) => upsertRequestedEntries(previous, freshIds, map))
    } catch (error) {
      // RN13 / RN7: a failed query changes nothing; rows keep their last known indicator and the
      // next refresh tries again. Non-blocking: no message to the user.
    }
  }, [])

  return [minimumStockStatusMap, resolveMinimumStockStatuses]
}

export default useMinimumStockStatus
